import EtgAccess from "../../features/etgAccess/etgAccess";
import { CallbackPage } from "../../features/etgAccess/CallbackPage";
import App from "../../App";
import PatientInfo from "../../features/patientInfo/patientInfo";
import TablesAndFigures from "../../features/tables-and-figures/tables-and-figures";
import ContactUs from "../../features/contact-us/ContactUs";
import Import from "../../features/import/Import";
import GenAI from "../../features/genai/genai";
import MyCart from "../../features/subscription/myCart";
import Checkout from "../../features/subscription/checkout";
import AccountChangePassword from "../AppAccount/AccountChangePassword";
import CheckoutConfirmation from "../../features/subscription/checkoutConfirmation";
import UserConfirmation from "../../features/subscription/userConfirmation";
import XeroAuth from "../../features/subscription/payment/xeroAuth";
import XeroAuthResponse from "../../features/subscription/payment/xeroAuthResponse";
import UserProfile from "../../features/userProfile/userProfile";
import { useTransition, a } from "@react-spring/web";
import { Route, Routes, useLocation } from "react-router-dom";
import TemplateMenu from "../templates/Menu";
import { useEffect, useRef } from "react";
import useAppModernisation from "../../hooks/useAppModernisation";
import { GuidelineTemplate } from "../Guideline/GuidelineTemplate";
import SearchRouter from "../../features/enhancedSearch/SearchRouter";
import SearchLandingPage from "../../features/appMode/SearchLandingPage";
import DrugLandingPage from "../../features/appMode/DrugLandingPage";
import AccountLandingPage from "../../features/appMode/AccountLandingPage";
import FavouritesPage from "../../features/appMode/FavouritesPage";
import AppSavedSearches from "../AppAccount/AppSavedSearches";
import DrugOverviewPage from "../../features/appMode/DrugOverviewPage";
import AboutUsAndLegal from "../AppAccount/AboutUsAndLegal";
import AccountSubscriptions from "../AppAccount/AccountSubscriptions";

const animateRoutes = ["/resources", "/calculator"];

const routes = [
  { path: "/assets", Component: EtgAccess },
  { path: "/etgAccess", Component: EtgAccess },
  { path: "/openathens-callback", Component: CallbackPage },
  { path: "/viewTopic", Component: App },
  { path: "/quicklinks", Component: App },
  { path: "/topicTeaser", Component: App },
  { path: "/searchAction", Component: SearchRouter },
  { path: "/app/searchLanding", Component: SearchLandingPage },
  { path: "/app/drugLanding", Component: DrugLandingPage },
  { path: "/app/accountLanding", Component: AccountLandingPage },
  { path: "/app/favourites", Component: FavouritesPage },
  { path: "/app/savedSearch", Component: AppSavedSearches },
  { path: "/app/aboutUsAndLegal", Component: AboutUsAndLegal },
  { path: "/app/changePassword", Component: AccountChangePassword },
  { path: "/app/accountSubscription", Component: AccountSubscriptions },
  { path: "/patientInfo", Component: PatientInfo },
  { path: "/tables-and-figures", Component: TablesAndFigures },
  { path: "/calculator/*", Component: TemplateMenu },
  { path: "/resources/*", Component: TemplateMenu },
  { path: "/contact", Component: ContactUs },
  { path: "/import", Component: Import },
  { path: "/genai", Component: GenAI },
  { path: "/myCart", Component: MyCart },
  { path: "/checkout", Component: Checkout },
  { path: "/checkout/orderConfirmation", Component: CheckoutConfirmation },
  { path: "/xeroAuth", Component: XeroAuth },
  { path: "/xeroAuth/response", Component: XeroAuthResponse },
  { path: "/showMyProfile", Component: UserProfile },
  { path: "/userConfirmation", Component: UserConfirmation },
  { path: "", Component: EtgAccess },
  { path: "/", Component: EtgAccess },
  { path: "/app/nav/*", Component: GuidelineTemplate },
  { path: "/app/drugOverview", Component: DrugOverviewPage },
];

const getAnimationProps = (current, previous, initial) => {
  let direction = "";
  let curr = current === "/etgAccess/" ? "/" : current;
  let prev = previous === "/etgAccess/" ? "/" : previous;
  const currLength = getPathDepth(curr);
  const prevLength = getPathDepth(prev);

  const isHome = curr === "/" || curr === "/etgAccess/";
  const wasHome = prev === "/" || prev === "/etgAccess/";

  const currentIsAnimated =
    animateRoutes.some((path) => curr.startsWith(path)) || isHome;
  const prevIsAnimated =
    animateRoutes.some((path) => prev.startsWith(path)) || wasHome;


  if (currentIsAnimated && prevIsAnimated && current !== previous) {
    if (currLength > prevLength) {
      // Going from animated path to home
      direction = "forward";
    } else {
      // Going from home to animated path
      direction = "backward";
    }
    if (initial === true) {
      direction = "";
    }
  } else {
    direction = "";
  }

  switch (direction) {
    case "forward":
      return {
        from: { opacity: 0, transform: "translate3d(100%, 0, 0)" },
        enter: { opacity: 1, transform: "translate3d(0%, 0, 0)" },
        leave: {
          opacity: 0,
          transform: "translate3d(-100%, 0, 0)",
          position: "absolute",
        },
      };
    case "backward":
      return {
        from: { opacity: 0, transform: "translate3d(-100%, 0, 0)" },
        enter: { opacity: 1, transform: "translate3d(0%, 0, 0)" },
        leave: {
          opacity: 0,
          transform: "translate3d(100%, 0, 0)",
          position: "absolute",
        },
      };
    default:
      return {
        from: { opacity: 1 },
        enter: { opacity: 1 },
        leave: { opacity: 1 },
      };
  }
};

const getPathDepth = (pathname) => {
  if (pathname === "/") return 0;
  return pathname.split("/").length;
};

const AnimatedRoute = () => {
  const location = useLocation();
  const { isAppMode } = useAppModernisation();

  const isInitialMount = useRef(true);
  const prevPathname = useRef("");
  useEffect(() => {
    prevPathname.current = location.pathname;
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
  }, [location.pathname]);

  useEffect(() => {
    if (isAppMode === true) {
      import("../../scss/appmode.scss");
    }
  }, [isAppMode]);

  const animationProps = getAnimationProps(
    location.pathname,
    prevPathname.current,
    isInitialMount.current
  );

  const transitions = useTransition(location, animationProps);

  const animatedRoutes = () => {
    return (
      <>
        {transitions((style, item) => {
          return (
            <a.div style={style} className={"animated-route"}>
              <Routes location={item}>
                {routes.map(({ path, Component }, index) => (
                  <Route key={index} {...{ path }} element={<Component />} />
                ))}
              </Routes>
            </a.div>
          );
        })}
      </>
    );
  };

  const nonAnimatedRoutes = () => {
    return (
      <Routes>
        {routes.map(({ path, Component }, index) => (
          <Route key={index} {...{ path }} element={<Component />} />
        ))}
      </Routes>
    );
  };
  return isAppMode ? animatedRoutes() : nonAnimatedRoutes();
};

export default AnimatedRoute;
